import React, { useEffect, useRef } from 'react';
import { Parallax } from 'react-parallax';
import { TextAnimate } from './TextAnimate';
import { useRAFScroll } from '../hooks/useRAFScroll';
import { mobileQuery } from '../utils/mediaQueries';
import './Who.css';

const PILLARS: readonly string[] = [
  'Site origination & feasibility',
  'Commercial structuring & grid connection strategy',
  'HV procurement & delivery',
  'Data centre design, construction & commissioning',
  'Project & construction management',
];
const PARALLAX_SPEEDS = { heading: 0.15, body: 0.15, pillars: 0.15 } as const;
const PARALLAX_SPEEDS_MOBILE = { heading: 0.09, body: 0.09, pillars: 0.09 } as const;

export default function Who(): React.JSX.Element {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const pillarsRef = useRef<HTMLDivElement>(null);
  // Cache section offset so getBoundingClientRect is not called every frame.
  const sectionTopRef = useRef<number>(0);

  useEffect(() => {
    const update = () => {
      if (sectionRef.current)
        sectionTopRef.current = sectionRef.current.getBoundingClientRect().top + window.scrollY;
    };
    update();
    window.addEventListener('resize', update, { passive: true });
    return () => window.removeEventListener('resize', update);
  }, []);

  useRAFScroll(() => {
    const section = sectionRef.current;
    if (!section) return;
    const delta = window.innerHeight / 2 - (sectionTopRef.current + section.offsetHeight / 2 - window.scrollY);
    const speeds = mobileQuery.matches ? PARALLAX_SPEEDS_MOBILE : PARALLAX_SPEEDS;

    if (headingRef.current)
      headingRef.current.style.transform = `translateY(${delta * speeds.heading}px)`;
    if (bodyRef.current)
      bodyRef.current.style.transform = `translateY(${delta * speeds.body}px)`;
    if (pillarsRef.current)
      pillarsRef.current.style.transform = `translateY(${delta * speeds.pillars}px)`;
  });

  return (
    <Parallax
      blur={{ min: -5, max: 5 }}
      strength={200}
      className="who"
      renderLayer={(percentage) => (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#e2e9ec',
            opacity: Math.min(1, percentage * 1.5),
          }}
        />
      )}
    >
      <section id="about" className="who__section" ref={sectionRef} aria-labelledby="who-heading">
        <div className="who__inner">
          <h2 id="who-heading" className="who__heading" ref={headingRef}>
            Infrastructure
          </h2>
          <p className="who__body" ref={bodyRef}>
            Everfield Group develops and delivers integrated energy infrastructure across Australia,
            specialising in grid-scale battery energy storage systems (BESS),
            renewable generation, and co-located AI-grade data centre capacity — purpose-built across strategic rural
            transmission corridors in the NEM.
          </p>
        </div>

        <div className="who__pillars" ref={pillarsRef} role="list" aria-label="Core capabilities">
          <div className="who__pillars-row">
            {PILLARS.slice(0, 3).map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="who__divider" aria-hidden="true" />}
                <TextAnimate
                  animation="slideUp"
                  by="word"
                  stagger={0.07}
                  each={0.4}
                  delay={0.3 + i * 0.1}
                  className="who__pillar"
                  as="div"
                >
                  {label}
                </TextAnimate>
              </React.Fragment>
            ))}
          </div>
          <div className="who__divider who__divider--row-gap" aria-hidden="true" />
          <div className="who__pillars-row who__pillars-row--two">
            {PILLARS.slice(3).map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="who__divider" aria-hidden="true" />}
                <TextAnimate
                  animation="slideUp"
                  by="word"
                  stagger={0.07}
                  each={0.4}
                  delay={0.6 + i * 0.1}
                  className="who__pillar"
                  as="div"
                >
                  {label}
                </TextAnimate>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
    </Parallax>
  );
}
